/**
 * Formats a location string with proper capitalization.
 *
 * Examples:
 * - "astoria, new york, ny, usa" → "Astoria, New York, NY, USA"
 * - "london, united kingdom" → "London, United Kingdom"
 * - "paris, france" → "Paris, France"
 * - "tokyo, japan" → "Tokyo, Japan"
 *
 * Rules:
 * 1. Split by comma to handle multi-level locations
 * 2. For each part, capitalize the first letter of each word
 * 3. Uppercase 2-letter state/region codes (NY, CA, ON, QC, etc.)
 * 4. Force "USA" to uppercase
 * 5. Handle common abbreviations (UK, EU, etc.)
 */

function isAbbreviationSegment(segment: string): boolean {
  return /^[a-z]{2,3}$/i.test(segment);
}

function formatLocationWord(word: string): string {
  const trimmed = word.trim();

  if (!trimmed) {
    return trimmed;
  }

  return trimmed
    .split(/([-'])/)
    .map((part) => {
      if (part === '-' || part === "'") {
        return part;
      }

      const lower = part.toLocaleLowerCase();

      return lower.charAt(0).toLocaleUpperCase() + lower.slice(1);
    })
    .join('');
}

function formatLocationSegment(segment: string): string {
  const trimmed = segment.trim();

  if (!trimmed) {
    return trimmed;
  }

  const lower = trimmed.toLowerCase();

  if (lower === 'usa' || lower === 'uk' || lower === 'eu') {
    return lower.toUpperCase();
  }

  if (isAbbreviationSegment(trimmed)) {
    return trimmed.toUpperCase();
  }

  return trimmed
    .split(/\s+/)
    .map(formatLocationWord)
    .join(' ');
}

/**
 * Formats a full location name with proper capitalization.
 * Handles comma-separated location parts (e.g., city, state, country)
 */
export default function formatLocationName(location: string): string {
  if (!location || typeof location !== 'string') {
    return location || '';
  }

  return location
    .split(',')
    .map(formatLocationSegment)
    .join(', ');
}

/**
 * Helper function to normalize location casing within a text string.
 * Useful for applying casing to already-rendered text that contains the location.
 *
 * This handles cases where the location appears within a template string
 * and needs to be corrected after template substitution.
 */
export function normalizeLocationCasingInText(
  text: string,
  rawLocation: string,
): string {
  if (!text || !rawLocation) return text;

  // Escape special regex characters
  const escaped = rawLocation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Create a case-insensitive regex to find the location anywhere in the text
  const regex = new RegExp(escaped, 'gi');

  // Replace with properly formatted location
  return text.replace(regex, formatLocationName(rawLocation));
}
